import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { InputField } from "@/components/InputField/InputField";
import { Icon } from "@/components/Icon/Icon";

import { DEFAULT_NEW_STEP_DAYS, FIRST_CONFIGURABLE_STEP } from "@/utils/stepDelayDefaults";

import { SendDelayEntry } from "../../../schema/copywritingSettings";

interface SendDelayDefaultsSectionProps {
	value: SendDelayEntry[];
	onChange: (value: SendDelayEntry[]) => void;
}

export const SendDelayDefaultsSection: FunctionComponent<SendDelayDefaultsSectionProps> = ({
	value,
	onChange,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`copywritingSettings.sendDelayDefaults.${key}`);

	const updateEntry = (index: number, field: keyof SendDelayEntry, val: string) => {
		const updated = [...value];
		const numValue = val === "" ? 0 : Number(val);

		updated[index] = { ...updated[index], [field]: numValue };
		onChange(updated);
	};

	// Step 1 is never configurable (it goes out in the next sending window), so rows start at 2.
	const addEntry = () => {
		const nextStep = Math.max(FIRST_CONFIGURABLE_STEP - 1, ...value.map((e) => e.step)) + 1;

		onChange([...value, { step: nextStep, delayDays: DEFAULT_NEW_STEP_DAYS }]);
	};

	return (
		<div className="flex flex-col gap-3">
			<p className="text-sm text-text-meta">{ts("description")}</p>

			{value.length > 0 && (
				<div className="flex items-center gap-3 text-xs font-medium text-text-meta uppercase tracking-wide">
					<div className="w-28">{ts("step")}</div>
					<div className="w-36">{ts("delayDays")}</div>
					<div className="w-8" />
				</div>
			)}

			{value.map((entry, index) => (
				<div key={index} className="flex items-start gap-3">
					<div className="w-28">
						<InputField
							handleChange={(e) => updateEntry(index, "step", e.target.value)}
							name={`step-${index}`}
							placeholder={ts("step")}
							showError={false}
							type="number"
							value={entry.step}
						/>
					</div>
					<div className="w-36">
						<InputField
							handleChange={(e) => updateEntry(index, "delayDays", e.target.value)}
							name={`delayDays-${index}`}
							placeholder={ts("delayDays")}
							showError={false}
							type="number"
							value={entry.delayDays}
						/>
					</div>
					<button
						className="mt-2 text-text-meta hover:text-status-error"
						type="button"
						onClick={() => onChange(value.filter((_, i) => i !== index))}
					>
						<Icon className="w-4 h-4" icon={faTrash} />
					</button>
				</div>
			))}

			<button
				className="flex items-center gap-1 text-sm text-blue-action hover:underline self-start"
				type="button"
				onClick={addEntry}
			>
				<Icon className="w-3 h-3" icon={faPlus} />
				{ts("addRow")}
			</button>
		</div>
	);
};
